import { CONVERSATION_ACTIVITY_EVENTS } from './messaging-internal-events';
import { MODEL_NAMES } from '../constants/index';
import { registerDestructor } from '@ember/destroyable';
import Service, { service } from '@ember/service';

/**
 * Handles conversation access-revoked activity-bus events.
 */
export default class ConversationAccessEventsService extends Service {
  @service intl;
  @service messaging;
  @service messagingInternalEvents;
  @service messagingLogging;
  @service messagingNavigation;
  @service store;
  @service('spds/toast') toast;

  #handledConversationIds = new Set();
  #viewerDisableInFlight = null;

  #onAccessRevoked = payload => this.handleAccessRevoked(payload);

  constructor() {
    super(...arguments);
    this.messagingInternalEvents.on(
      CONVERSATION_ACTIVITY_EVENTS.ACCESS_REVOKED,
      this.#onAccessRevoked
    );

    registerDestructor(this, () => {
      this.messagingInternalEvents.off(
        CONVERSATION_ACTIVITY_EVENTS.ACCESS_REVOKED,
        this.#onAccessRevoked
      );
    });
  }

  handleAccessRevoked = async ({ conversationId, showToast = true }) => {
    const id = String(conversationId);
    if (this.#handledConversationIds.has(id)) {
      return;
    }
    this.#handledConversationIds.add(id);

    const wasViewing = this.messagingNavigation.isViewingConversation(id);

    try {
      this.messagingNavigation.invalidateHistory(id);
      this.messaging.removeConversationFromList(id);
      this.messaging.currentProfile?.removeUnreadConversation(id, { shouldTrack: false });

      if (wasViewing) {
        await this.messagingNavigation.toConversationList({ recordHistory: false });
        if (showToast) {
          this.toast.error(this.intl.t('emc.conversation_no_longer_available'));
        }
      }
    } catch (err) {
      this.messagingLogging.error(err, { conversationId });
    } finally {
      this.#unloadConversation(id);
    }
  };

  handleViewerMessagingDisabled = async () => {
    if (this.#viewerDisableInFlight) {
      return this.#viewerDisableInFlight;
    }

    this.#viewerDisableInFlight = this.#revokeAllVisibleConversations();
    try {
      await this.#viewerDisableInFlight;
    } finally {
      this.#viewerDisableInFlight = null;
    }
  };

  async #revokeAllVisibleConversations() {
    const current = this.messagingNavigation.currentConversationHandler?.conversation;
    const wasViewing = Boolean(current);
    const conversations = [...(this.messaging.conversations?.content ?? [])];

    try {
      conversations.forEach(conversation => this.#dropConversationFromUi(conversation));

      if (wasViewing) {
        await this.messagingNavigation.toConversationList({ recordHistory: false });
        this.toast.error(this.intl.t('emc.conversation_no_longer_available'));
      }
    } catch (err) {
      this.messagingLogging.error(err, { context: 'viewer-messaging-disabled' });
    } finally {
      conversations.forEach(conversation => this.#unloadConversationRecord(conversation));
    }
  }

  #dropConversationFromUi(conversation) {
    if (conversation.id !== null && conversation.id !== undefined) {
      this.#handledConversationIds.add(String(conversation.id));
      this.messagingNavigation.invalidateHistory(conversation.id);
      this.messaging.currentProfile?.removeUnreadConversation(conversation.id, {
        shouldTrack: false,
      });
    }
    this.messaging.removeConversationRecord(conversation);
  }

  #unloadConversationRecord(conversation) {
    if (!conversation.isDestroyed && !conversation.isDestroying) {
      this.store.unloadRecord(conversation);
    }
  }

  #unloadConversation(id) {
    const record = this.store.peekRecord(MODEL_NAMES.CONVERSATION, id);
    if (!record) {
      return;
    }

    this.store.unloadRecord(record);
  }
}
